// In-memory warehouse structure for demo mode (no Supabase configured).
// Mirrors the database rules the UI depends on: DEFAULT location per
// warehouse, codes unique per company / warehouse, keyset paging on code,
// bulk creation skipping existing codes.

import type { Page } from "@/components/DataList";
import type {
  LocationInput,
  LocationListParams,
  LocationRow,
  LocationUpdate,
  Site,
  WarehouseInput,
  WarehouseRow,
  WarehouseUpdate,
} from "./api";

const PAGE = 50;
const COMPANY = "demo-pl01";
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
const now = () => new Date().toISOString();
let seq = 0;
const nextId = (prefix: string) => `${prefix}-${++seq}`;

const duplicate = () => Object.assign(new Error("duplicate key"), { code: "23505" });

const sites: Site[] = [{ id: "site-LDZ", company_id: COMPANY, code: "LDZ", name: "Zakład Łódź", is_active: true }];

type StoredWarehouse = Omit<WarehouseRow, "site" | "company">;
const warehouses: StoredWarehouse[] = (
  [
    ["MAIN", "Magazyn główny", "main", false],
    ["PROD", "Magazyn produkcyjny", "production", true],
    ["QUAR", "Kwarantanna", "quarantine", false],
  ] as const
).map(([code, name, warehouse_type, allow_negative]) => ({
  id: `wh-${code}`,
  company_id: COMPANY,
  site_id: "site-LDZ",
  code,
  name,
  warehouse_type,
  allow_negative,
  is_nettable: warehouse_type !== "quarantine",
  is_active: true,
  supplier_id: null,
  supplier: null,
  quarantine_location_id: null,
  quarantine_location: null,
  updated_at: now(),
}));

const locations: LocationRow[] = [];

function addLocation(warehouseId: string, input: Partial<LocationRow> & { code: string }): LocationRow {
  const row: LocationRow = {
    id: nextId("loc"),
    warehouse_id: warehouseId,
    description: null,
    zone: null,
    is_receivable: true,
    is_pickable: true,
    is_blocked: false,
    is_default: false,
    is_active: true,
    updated_at: now(),
    ...input,
  };
  locations.push(row);
  return row;
}

for (const w of warehouses) addLocation(w.id, { code: "DEFAULT", is_default: true });
for (const zone of ["A", "B"])
  for (let bay = 1; bay <= 6; bay++)
    for (let lvl = 1; lvl <= 4; lvl++)
      addLocation("wh-MAIN", { code: `${zone}-${String(bay).padStart(2, "0")}-${String(lvl).padStart(2, "0")}`, zone });
addLocation("wh-PROD", { code: "LINE-1", description: "Linia montażu 1", zone: "LINES" });
addLocation("wh-PROD", { code: "LINE-2", description: "Linia montażu 2", zone: "LINES" });
addLocation("wh-PROD", { code: "KIT", description: "Kompletacja" });
addLocation("wh-QUAR", { code: "Q-01", description: "Strefa kontroli 1", zone: "QC", is_pickable: false });
addLocation("wh-QUAR", { code: "Q-02", description: "Strefa kontroli 2", zone: "QC", is_pickable: false });

function toWarehouseRow(w: StoredWarehouse): WarehouseRow {
  const site = sites.find((s) => s.id === w.site_id);
  return { ...w, site: site ? { code: site.code, name: site.name } : null, company: { code: "PL01" } };
}

export const demoApi = {
  async listSites(): Promise<Site[]> {
    await delay(30);
    return [...sites].sort((a, b) => (a.code < b.code ? -1 : 1));
  },
  async listWarehouses(inactive: boolean): Promise<Page<WarehouseRow>> {
    await delay(60);
    const rows = warehouses
      .filter((w) => inactive || w.is_active)
      .sort((a, b) => (a.code < b.code ? -1 : 1))
      .map(toWarehouseRow);
    return { rows, nextCursor: null };
  },
  async getWarehouse(id: string): Promise<WarehouseRow | null> {
    await delay(40);
    const w = warehouses.find((x) => x.id === id);
    return w ? toWarehouseRow(w) : null;
  },
  async listLocations(p: LocationListParams, cursor: string | null): Promise<Page<LocationRow>> {
    await delay(60);
    const q = p.q.trim().toUpperCase();
    const filtered = locations
      .filter((l) => l.warehouse_id === p.warehouseId)
      .filter((l) => p.inactive || l.is_active)
      .filter((l) => !q || l.code.startsWith(q))
      .sort((a, b) => (a.code < b.code ? -1 : 1))
      .filter((l) => !cursor || l.code > cursor);
    const page = filtered.slice(0, PAGE);
    return { rows: page, nextCursor: filtered.length > PAGE ? page[page.length - 1]!.code : null };
  },
  async getLocation(id: string): Promise<LocationRow | null> {
    await delay(40);
    return locations.find((l) => l.id === id) ?? null;
  },
  async createSite(input: { company_id: string; code: string; name: string }): Promise<string> {
    await delay(100);
    if (sites.some((s) => s.company_id === input.company_id && s.code === input.code)) throw duplicate();
    const id = `site-${input.code}`;
    sites.push({ ...input, id, is_active: true });
    return id;
  },
  async createWarehouse(input: WarehouseInput): Promise<string> {
    await delay(120);
    if (warehouses.some((w) => w.company_id === input.company_id && w.code === input.code)) throw duplicate();
    const id = `wh-${input.code}`;
    warehouses.push({
      ...input,
      id,
      is_nettable: input.warehouse_type !== "quarantine" && input.warehouse_type !== "transit",
      is_active: true,
      quarantine_location_id: null,
      quarantine_location: null,
      supplier: null,
      updated_at: now(),
    });
    addLocation(id, { code: "DEFAULT", is_default: true });
    return id;
  },
  async updateWarehouse(id: string, input: WarehouseUpdate): Promise<void> {
    await delay(100);
    const w = warehouses.find((x) => x.id === id);
    if (w) Object.assign(w, input, { updated_at: now() });
  },
  async createLocation(warehouseId: string, input: LocationInput): Promise<string> {
    await delay(100);
    if (locations.some((l) => l.warehouse_id === warehouseId && l.code === input.code)) throw duplicate();
    return addLocation(warehouseId, input).id;
  },
  async updateLocation(id: string, input: LocationUpdate): Promise<void> {
    await delay(100);
    const l = locations.find((x) => x.id === id);
    if (!l) return;
    if (l.is_default && (!input.is_active || input.is_blocked || !input.is_receivable || !input.is_pickable)) {
      throw Object.assign(new Error("locations_default_usable"), { code: "23514" });
    }
    Object.assign(l, input, { updated_at: now() });
  },
  async createLocations(
    warehouseId: string,
    codes: string[],
    zone: string | null,
    isReceivable: boolean,
    isPickable: boolean,
  ): Promise<number> {
    await delay(150);
    const existing = new Set(locations.filter((l) => l.warehouse_id === warehouseId).map((l) => l.code));
    let created = 0;
    for (const code of new Set(codes)) {
      if (existing.has(code)) continue;
      addLocation(warehouseId, { code, zone, is_receivable: isReceivable, is_pickable: isPickable });
      created++;
    }
    return created;
  },
};
